/** Keep browser event properties aligned with the clinician gateway allowlist. */
const BOOLEAN_PROPERTIES = new Set([
  'mfa', 'quick_add', 'has_phone', 'has_email', 'is_new', 'move_reports',
  'move_logging_periods', 'has_filters',
]);

const COUNT_PROPERTIES = new Map([
  ['tip_length', 300],
  ['item_count', 100],
  ['excludes_count', 30],
]);

const ENUM_PROPERTIES = new Map<string, ReadonlySet<string>>([
  ['destination', new Set(['knowledge_base'])],
  ['filter', new Set([
    'status', 'flag', 'addedBy', 'clinic', 'search', 'clinics', 'limit',
    'logType', 'mealType', 'nutrition', 'glucose',
  ])],
  ['goal_type', new Set(['nutrient', 'frequency'])],
  ['method', new Set(['clipboard', 'email'])],
  ['Report Section', new Set(['Clinician Generated', 'RxFood Generated', 'Sent'])],
  ['tab', new Set([
    'summary', 'logs', 'logs-v2', 'nutrients', 'prescriptions', 'recipes',
    'meal-plans', 'emotions', 'reports', 'resources', 'documents', 'subscriptions',
  ])],
  ['preset', new Set(['7d', '30d', '90d', 'month', 'custom'])],
  ['status', new Set(['draft', 'ready to send', 'sent'])],
  ['variant', new Set(['recipes', 'meal-plan', 'food-substitutions', 'custom-search'])],
]);

const CHANGED_FIELDS = new Set(['first_name', 'last_name', 'email', 'phone_number', 'bio']);

export function sanitizeSegmentation(
  properties: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (BOOLEAN_PROPERTIES.has(key)) {
      if (typeof value === 'boolean') out[key] = value;
      continue;
    }

    const maxCount = COUNT_PROPERTIES.get(key);
    if (maxCount !== undefined) {
      if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= maxCount) {
        out[key] = value;
      }
      continue;
    }

    const values = ENUM_PROPERTIES.get(key);
    if (values) {
      if (typeof value === 'string' && values.has(value)) out[key] = value;
      continue;
    }

    if (key === 'changed_fields' && typeof value === 'string') {
      const fields = value.split(',');
      if (fields.length <= 5 && fields.every(field => CHANGED_FIELDS.has(field))) {
        out[key] = value;
      }
    }
  }
  return out;
}

/** Mask opaque patient route IDs and whole numeric path segments. */
export function parameterizePath(pathname: string): string {
  return pathname
    .replace(/(^|\/)patients\/[^/]+/g, '$1patients/:id')
    .replace(/(^|\/)cgm-report\/[^/]+/g, '$1cgm-report/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
}

/** Remove query/hash secrets and mask path identifiers before Sentry delivery. */
export function scrubSensitiveUrl(url: string): string {
  const withoutQueryOrHash = url.split(/[?#]/, 2)[0] ?? url;
  return parameterizePath(withoutQueryOrHash);
}
